import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Space,
  Typography,
  Input,
  Button,
  Table,
  Tag,
  Statistic,
  message,
} from 'antd';
import {
  GlobalOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const WhiteLabelDomain = () => {
  const [domain, setDomain] = useState('agency.yourdomain.com');
  const [verifying, setVerifying] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  const cnameTarget = useMemo(() => 'cname.pathmarkr.app', []);
  const apexTarget = useMemo(() => 'apex.pathmarkr.app', []);

  const records = useMemo(() => ([
    { key: '1', type: 'CNAME', name: domain.split('.').slice(0, -2).join('.') || '@', value: cnameTarget, status: 'Pending' },
    { key: '2', type: 'A', name: '@', value: '76.76.21.21', status: 'OK' },
    { key: '3', type: 'TXT', name: '@', value: 'pm-verification=abc123', status: 'Pending' },
  ]), [domain, cnameTarget]);

  const columns = [
    { title: 'Type', dataIndex: 'type', key: 'type', width: 90 },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 140 },
    { title: 'Value', dataIndex: 'value', key: 'value', render: (v) => (
      <Space>
        <code>{v}</code>
        <Button size="small" icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(v)}>
          Copy
        </Button>
      </Space>
    ) },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => (
      <Tag color={s === 'OK' ? 'green' : 'default'}>{s}</Tag>
    ) },
  ];

  const kpis = useMemo(() => {
    const ok = records.filter(r => r.status === 'OK').length;
    const pending = records.length - ok;
    const ssl = ok >= 2 ? 'Active' : 'Not provisioned';
    return { ok, pending, ssl };
  }, [records]);

  const verifyDNS = async () => {
    try {
      setVerifying(true);
      // TODO: call backend to verify DNS: POST /api/whitelabel/domain/verify { domain }
      await new Promise(r => setTimeout(r, 800));
      message.success('DNS verification started. This can take a few minutes to propagate.');
    } finally {
      setVerifying(false);
    }
  };

  const provisionSSL = async () => {
    try {
      setProvisioning(true);
      // TODO: call backend to provision SSL: POST /api/whitelabel/domain/provision-ssl { domain }
      await new Promise(r => setTimeout(r, 1000));
      message.success('SSL provisioning queued.');
    } finally {
      setProvisioning(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Domain</Title>
          <Text type="secondary">Configure custom domain and SSL for your white-label service.</Text>
        </div>

        {/* KPIs */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Space>
                <GlobalOutlined />
                <Statistic title="Records OK" value={kpis.ok} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Space>
                <ReloadOutlined />
                <Statistic title="Pending" value={kpis.pending} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Space>
                {kpis.ssl === 'Active' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                <Statistic title="SSL" value={kpis.ssl} />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Domain controls */}
        <Card title="Domain Settings" extra={<SafetyCertificateOutlined />}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={10}>
              <Input addonBefore="https://" value={domain} onChange={(e) => setDomain(e.target.value)} />
            </Col>
            <Col xs={24} md={14} style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={verifyDNS} loading={verifying}>Verify DNS</Button>
                <Button type="primary" onClick={provisionSSL} loading={provisioning}>Provision SSL</Button>
              </Space>
            </Col>
          </Row>

          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col span={24}>
              <Text type="secondary">CNAME target</Text>
              <div>
                <code>{cnameTarget}</code>
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">Apex/Root target</Text>
              <div>
                <code>{apexTarget}</code>
              </div>
            </Col>
          </Row>
        </Card>

        {/* DNS Records */}
        <Card title="Required DNS Records">
          <Table rowKey="key" columns={columns} dataSource={records} pagination={false} />
        </Card>
      </Space>
    </div>
  );
};

export default WhiteLabelDomain;
